import { AlertTriangle, Eye, GitBranch, Lightbulb, Trophy } from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * The insight type contract (#601) — the single source of truth for the five
 * flagged-insight types. Colors, label, icon, and header-dot color are all keyed
 * off the type; the API sends only the key. The FlaggedInsights panel, the
 * chapter-header dot row, and any future digest view all read this map rather
 * than re-declaring the hex values.
 *
 * These colors are intentionally *not* the semantic design tokens (theme.ts /
 * @thegpvc/ui Badge variants); they are tuned warmer to sit on the cream panel.
 */

export type InsightType = 'observation' | 'recommendation' | 'decision' | 'anomaly' | 'win';

export interface Insight {
  id: string;
  type: InsightType;
  title: string; // one line, sentence case, no trailing period
  body: string; // 1–2 sentences
  cta?: { label: string; href: string };
}

export interface InsightTypeSpec {
  label: string;
  Icon: ComponentType<{ size?: number | string; className?: string; 'aria-hidden'?: boolean }>;
  text: string; // pill text color
  fill: string; // pill background
  border: string; // pill border
  dot: string; // chapter-header dot color (shared source of truth)
}

export const INSIGHT_TYPES: Record<InsightType, InsightTypeSpec> = {
  observation: { label: 'Observation', Icon: Eye, text: '#2f5b8a', fill: '#eef4fb', border: '#cfe0f2', dot: '#5b9bd5' },
  recommendation: { label: 'Recommendation', Icon: Lightbulb, text: '#c2410c', fill: '#fff5ec', border: '#ffd9b8', dot: '#ff6a00' },
  decision: { label: 'Open decision', Icon: GitBranch, text: '#a15c07', fill: '#fef7e6', border: '#f6dfa8', dot: '#f59e0b' },
  anomaly: { label: 'Anomaly', Icon: AlertTriangle, text: '#be123c', fill: '#fff1f3', border: '#fbcfd6', dot: '#f43f5e' },
  win: { label: 'Highlight', Icon: Trophy, text: '#047857', fill: '#ecfdf5', border: '#c3f0dc', dot: '#10b981' },
};

/** Unknown key → fall back to `observation` and warn (closed set contract). */
export function resolveInsightType(type: InsightType): InsightTypeSpec {
  const spec = INSIGHT_TYPES[type];
  if (!spec) {
    console.warn(`insightTypes: unknown insight type "${type}", falling back to "observation"`);
    return INSIGHT_TYPES.observation;
  }
  return spec;
}
